import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../cn.js';

// ---------- Table ----------

/**
 * stickyHeader: tabela longa rola dentro da própria caixa (até 70% da tela) e o cabeçalho fica parado.
 * Sem ele, a caixa só rola para o lado e o cabeçalho rola junto com a página.
 */
export function Table({ className, stickyHeader = false, ...props }: React.TableHTMLAttributes<HTMLTableElement> & { stickyHeader?: boolean }) {
  return (
    <div
      className={cn(
        // relative: o rótulo sr-only (absoluto) de uma coluna fica dentro da rolagem, e não alarga a página no celular.
        'relative w-full overflow-x-auto rounded-[var(--card-radius)] border border-border',
        stickyHeader && 'max-h-[70vh] overflow-y-auto [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:shadow-[0_1px_0_var(--border)]',
      )}
    >
      <table className={cn('w-full border-collapse text-[length:var(--lr-text-base)]', className)} {...props} />
    </div>
  );
}

export function TableCaption({ className, ...props }: React.HTMLAttributes<HTMLTableCaptionElement>) {
  return <caption className={cn('sr-only', className)} {...props} />;
}

export function TableHeader(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className="bg-[var(--table-header-surface)]" {...props} />;
}

export function TableBody(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className="[&>tr:hover]:bg-sunken/60" {...props} />;
}

export function TableRow({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('border-b border-border transition-colors duration-150 ease-out last:border-b-0', className)} {...props} />;
}

export function TableHead({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn('h-9 whitespace-nowrap px-3 text-left text-[length:var(--lr-text-sm)] font-medium text-fg-secondary', className)}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('h-[var(--table-row-height)] px-3 py-2 align-middle', className)} {...props} />;
}

// ---------- Badge ----------

export const badgeVariants = cva('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[length:var(--lr-text-xs)] font-medium [&_svg]:size-3', {
  variants: {
    tone: {
      neutral: 'bg-sunken text-fg-secondary',
      accent: 'bg-accent-soft text-fg',
      success: 'bg-success-surface text-success',
      warning: 'bg-warning-surface text-warning',
      danger: 'bg-danger-surface text-danger',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

export function Badge({ className, tone, ...props }: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

// ---------- Skeleton ----------

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div aria-hidden="true" className={cn('animate-pulse rounded-[var(--lr-radius-md)] bg-sunken', className)} {...props} />;
}

// ---------- Alert ----------

const ICONS = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: XCircle } as const;

const alertTone = {
  info: 'border-border bg-surface-raised text-fg',
  success: 'border-success/40 bg-success-surface text-fg',
  warning: 'border-warning/40 bg-warning-surface text-fg',
  danger: 'border-danger/40 bg-danger-surface text-fg',
} as const;

const iconTone = { info: 'text-fg-secondary', success: 'text-success', warning: 'text-warning', danger: 'text-danger' } as const;

/** Mensagem com ícone e título: estado nunca depende só da cor. */
export function Alert({
  tone = 'info',
  title,
  children,
  className,
  role,
}: {
  tone?: keyof typeof ICONS;
  title: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  role?: 'alert' | 'status';
}) {
  const Icon = ICONS[tone];
  return (
    <div role={role ?? (tone === 'danger' ? 'alert' : 'status')} className={cn('flex gap-3 rounded-[var(--card-radius)] border p-3', alertTone[tone], className)}>
      <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', iconTone[tone])} />
      <div className="flex min-w-0 flex-col gap-1 text-[length:var(--lr-text-base)]">
        <p className="font-semibold">{title}</p>
        {children ? <div className="text-fg-secondary">{children}</div> : null}
      </div>
    </div>
  );
}
